import { MapPin, ShoppingCart, Search, Percent } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { useState, useEffect, useRef } from 'react';
import { useCart } from '@/contexts/CartContext';
import { useMarketplaceSearch } from '@/hooks/useMarketplaceSearch';
import MarketplaceSearchResults from '@/components/MarketplaceSearchResults';

const MarketplaceTopNavigation = () => {
  const { getMarketplaceCartCount } = useCart();
  const [selectedLocation, setSelectedLocation] = useState<string>('Select location');
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { sellers, stockItems, loading } = useMarketplaceSearch(searchQuery);

  useEffect(() => {
    // Light-weight location read (no Supabase fetches here)
    const savedLocationStr = localStorage.getItem('selectedLocation');
    if (savedLocationStr) {
      try {
        const location = JSON.parse(savedLocationStr);
        const address: string = location.address || location.address_line || 'Select location';
        setSelectedLocation(address.length > 25 ? `${address.substring(0, 25)}...` : address);
      } catch (e) {
        setSelectedLocation('Select location');
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowResults(value.length >= 2);
  };

  const handleSearchFocus = () => {
    if (searchQuery.length >= 2) setShowResults(true);
  };

  const handleNavClick = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className="sticky top-0 bg-background border-b border-border z-40 px-4 py-3" onClick={handleNavClick}>
      <div className="flex items-center justify-between mb-3">
        <Link
          to="/location"
          className="flex items-center gap-2 text-foreground/80 hover:bg-accent px-2 py-1 rounded-md transition-colors"
          onClick={handleNavClick}
        >
          <MapPin className="h-5 w-5" />
          <div className="text-left">
            <span className="text-xs block">Deliver to</span>
            <span className="text-sm font-medium block">{selectedLocation}</span>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          <Link
            to="/marketplace/deals"
            className="text-foreground/80 hover:bg-accent p-2 rounded-md transition-colors"
            onClick={handleNavClick}
            aria-label="Marketplace deals"
          >
            <Percent className="h-6 w-6" />
          </Link>

          <Link
            to="/marketplace/cart"
            className="text-foreground/80 relative hover:bg-accent p-2 rounded-md transition-colors"
            onClick={handleNavClick}
            aria-label="Marketplace cart"
          >
            <ShoppingCart className="h-6 w-6" />
            {getMarketplaceCartCount() > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full text-xs w-5 h-5 flex items-center justify-center font-bold">
                {getMarketplaceCartCount()}
              </span>
            )}
          </Link>
        </div>
      </div>

      <div className="relative" ref={searchRef}>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
        <Input
          placeholder="Search sellers or products"
          className="pl-10 bg-muted border-border"
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          aria-label="Search sellers or products"
        />
        {showResults && (
          <MarketplaceSearchResults
            sellers={sellers}
            stockItems={stockItems}
            loading={loading}
            onClose={() => setShowResults(false)}
          />
        )}
      </div>
    </div>
  );
};

export default MarketplaceTopNavigation;
